import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StarRating from './StarRating';
import MasterPage from '../../homeDashboard/templates/CustomerPage';
import { submitRating } from "../../Services/ApiCallsServices/FeedbackApi";
import { fetchTripStatus } from "../../Services/ApiCallsServices/HomeDashboardApi";
import { useSelector } from "react-redux";
import { useAuth } from "../../context/AuthContext";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

const Feedback = () => {
  const tripReference = useSelector((state) => state.notification.selectedTripRef);
  const [tripRating, setTripRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState('Completed');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const navigate = useNavigate();
  const { auth } = useAuth();
  const fullName = auth.user?.first_name + ' ' + auth.user?.last_name;

  useEffect(() => {
    const getStatus = async () => {
      try {
        const response = await fetchTripStatus(tripReference);
        setStatus(response.status);
      } catch (error) {
        setError('Error fetching trip status.');
        console.error("Trip status error:", error);
      }
    };

    if (tripReference) getStatus();
  }, [tripReference]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const ratingsData = {
      reference: tripReference,
      trip_rating: tripRating,
      comment: comment,
      name: fullName,
      occupation: "Customer",
    };

    try {
      await submitRating(ratingsData);
      toast.success('Feedback submitted!');
      setSubmitted(true);
      setIsSubmitting(false);

      setTimeout(() => {
        setShowThankYou(true);
      }, 1500);

      setTimeout(() => {
        navigate('/dashboard');
      }, 6000);

    } catch (error) {
      console.error('Error submitting ratings:', error);
      toast.error('Failed to submit feedback.');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setTripRating(0);
    setComment('');
    navigate(-1);
  };

  if (error) {
    return (
        <MasterPage>
          <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-md p-6 text-center">
              <h1 className="text-xl font-semibold text-red-600 mb-4">Error</h1>
              <p className="text-gray-700 mb-6">{error}</p>
              <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-5 py-2 rounded-lg transition"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </MasterPage>
    );
  }

  if (status !== 'Completed') {
    return (
        <MasterPage>
          <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-md p-6 text-center">
              <h1 className="text-xl font-semibold text-gray-800 mb-4">Feedback Not Available</h1>
              <p className="text-gray-700 mb-6">
                Feedback can only be submitted after the trip is completed.
              </p>
              <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-5 py-2 rounded-lg transition"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </MasterPage>
    );
  }

  return (
      <MasterPage>
        <Helmet>
          <title>Feedback | Storage Sprint</title>
        </Helmet>

        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                <span className="text-yellow-500">We Value </span>Your Feedback
              </h1>
              <p className="text-gray-600">
                Help us improve by sharing your experience with this trip.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden p-8">
              {showThankYou ? (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <svg
                        className="h-16 w-16 text-green-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <h2 className="text-2xl font-bold text-gray-800">Thank You!</h2>
                    <p className="text-gray-600 text-center max-w-sm">
                      We appreciate your feedback. It helps us improve our service for future trips.
                    </p>
                    <p className="text-sm text-gray-400">Redirecting to your dashboard shortly...</p>
                  </div>
              ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div>
                      <label className="block text-gray-800 font-semibold mb-3 text-lg text-center">
                        Rate Your Experience
                      </label>
                      <div className="flex flex-col items-center">
                        <StarRating
                            rating={tripRating}
                            setRating={setTripRating}
                            hover={hover}
                            setHover={setHover}
                        />
                        <p className="mt-2 text-yellow-600 font-semibold text-lg select-none">
                          {tripRating} {tripRating === 1 || tripRating === 0 ? 'Star' : 'Stars'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="comment" className="block text-gray-800 font-semibold mb-3 text-lg">
                        Your Feedback <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                          id="comment"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          rows={5}
                          maxLength={500}
                          placeholder="Tell us what you liked or what we can improve..."
                          className="w-full border border-gray-300 rounded-xl p-4 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-yellow-400 transition resize-none shadow-sm"
                          disabled={isSubmitting}
                      />
                    </div>

                    <div className="flex justify-between">
                      <button
                          type="button"
                          onClick={handleCancel}
                          className="px-8 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition font-semibold"
                          disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                          type="submit"
                          disabled={tripRating === 0 || isSubmitting || submitted}
                          className={`px-8 py-3 rounded-xl font-semibold text-white transition flex items-center justify-center space-x-2 w-40
                      ${submitted
                              ? 'bg-green-500'
                              : isSubmitting
                                  ? 'bg-yellow-400 cursor-wait opacity-80'
                                  : tripRating === 0
                                      ? 'bg-yellow-300 cursor-not-allowed'
                                      : 'bg-yellow-500 hover:bg-yellow-600'
                          }`}
                      >
                        {isSubmitting ? (
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                              <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                              />
                              <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              />
                            </svg>
                        ) : null}
                        <span>{submitted ? 'Submitted' : isSubmitting ? 'Submitting...' : 'Submit'}</span>
                      </button>
                    </div>
                  </form>
              )}
            </div>
          </div>
        </div>
      </MasterPage>
  );
};

export default Feedback;
